import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Users, ShieldCheck, Shield, KeyRound, UserCheck, Eye, Copy, UserPlus, Trash2, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";
import AdHocFeedbackDialog from "@/components/AdHocFeedbackDialog";

const USER_TYPE_LABELS = {
  employee: "Employee", client: "Client", vendor: "Vendor",
  contractor: "Contractor", partner: "Partner",
};

// Default role options used as a fallback if workspace_roles fetch fails.
const FALLBACK_ROLE_OPTIONS = [
  { v: "workspace_owner", label: "Workspace Owner" },
  { v: "workspace_admin", label: "Workspace Admin" },
  { v: "manager", label: "Manager" },
  { v: "team_member", label: "Team Member" },
  { v: "guest", label: "Guest" },
];

export default function UserManagement() {
  const { activeWorkspaceId, activeWorkspace, user: me } = useAuth();
  const { can } = useWorkspacePerms();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filterSource, setFilterSource] = useState("");
  const [inspecting, setInspecting] = useState(null);
  const [creating, setCreating] = useState(false);
  const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLE_OPTIONS);
  // Feb 2026 · Track whether the roles API actually responded — used to
  // decide whether to render the "Custom roles couldn't be loaded" banner.
  // `null` = still loading; `true` = last fetch succeeded (even if empty);
  // `false` = last fetch errored.
  const [rolesApiOk, setRolesApiOk] = useState(null);
  const [rolesApiEmpty, setRolesApiEmpty] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // {action, label}
  const [canGiveFeedback, setCanGiveFeedback] = useState(false);
  const isOwner = activeWorkspace?.role === "owner" || activeWorkspace?.owner_id === me?.id;
  // Feb 2026 · Include granular perms so custom roles (e.g. HR Manager with
  // `workspace.member.manage` or `hr.manage`) can manage users. Previously
  // only the string role slugs `owner` / `admin` unlocked this — a hard-coded
  // gate that broke non-standard workspace role blueprints.
  const isAdmin = isOwner
    || activeWorkspace?.role === "admin"
    || can("workspace.member.manage")
    || can("hr.manage");

  // Iter 253z-2 · Load can-give-feedback once per workspace so row-level
  // Give Feedback shortcut can be rendered.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/feedback/can-create-internal`)
      .then((r) => setCanGiveFeedback(!!r.data?.allowed))
      .catch(() => setCanGiveFeedback(false));
  }, [activeWorkspaceId]);

  // Pull workspace-level roles so the dropdown reflects the actual access tiers.
  // Feb 2026 · Merge (never REPLACE) with the FALLBACK list so built-in
  // roles always show even if the API returns empty or the workspace's
  // `workspace_roles` collection was accidentally wiped. Any custom role
  // returned by the API wins the dedupe (its label + description trump
  // the fallback's static label).
  const loadRoles = () => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/roles`)
      .then((r) => {
        const apiRows = Array.isArray(r.data) ? r.data : [];
        // Feb 2026 — Portalwiz prod: never DROP a returned row. If a
        // custom role somehow lands with an empty slug we still keep
        // it (using id as a fallback key + label) so the admin can
        // see it and repair it instead of thinking it vanished.
        const apiOpts = apiRows.map((row) => {
          const slug = (row && (row.slug || row.id || "")).toString().trim();
          const name = (row && (row.name || row.slug || "")).toString().trim();
          return {
            v: slug || `unknown_${row?.id || Math.random().toString(36).slice(2)}`,
            label: name || (slug ? `(unnamed · ${slug})` : "(unnamed role)"),
            description: row?.description,
            is_active: row?.is_active !== false,
            is_system: !!row?.is_system,
            source: "api",
          };
        });
        // Log any suspicious row so support can see it once with F12.
        const shady = apiRows.filter((r) => !(r && (r.slug || r.id) && r.name));
        if (shady.length > 0) {
          try { console.warn("[Roles] Rows with missing slug/name — still kept", shady); } catch { /* no-op */ }
        }
        try { window.__acquilix_last_roles = { raw: apiRows, mapped: apiOpts, at: new Date().toISOString() }; } catch { /* no-op */ }
        const bySlug = new Map(apiOpts.map((o) => [o.v, o]));
        for (const fb of FALLBACK_ROLE_OPTIONS) {
          if (!bySlug.has(fb.v)) bySlug.set(fb.v, { ...fb, source: "fallback" });
        }
        setRoleOptions(Array.from(bySlug.values()));
        setRolesApiOk(true);
        setRolesApiEmpty(apiOpts.length === 0);
      })
      .catch((err) => {
        try { window.__acquilix_last_roles_err = err?.response?.data || err?.message; } catch { /* no-op */ }
        setRoleOptions(FALLBACK_ROLE_OPTIONS.map((f) => ({ ...f, source: "fallback" })));
        setRolesApiOk(false);
        setRolesApiEmpty(false);
      });
  };
  useEffect(() => { loadRoles(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/users-admin`);
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const filtered = useMemo(() => {
    if (!filterSource) return rows;
    return rows.filter((r) => r.source === filterSource);
  }, [rows, filterSource]);

  const counts = useMemo(() => {
    const out = { all: rows.length, internal: 0, contact: 0 };
    rows.forEach((r) => { if (r.source === "internal") out.internal++; else out.contact++; });
    return out;
  }, [rows]);

  const roleLabelFor = useMemo(() => {
    const m = new Map((roleOptions || []).map((o) => [o.v, o.label]));
    return (slug) => m.get(slug) || slug || "—";
  }, [roleOptions]);

  const columns = useMemo(() => [
    { key: "name", label: "Name · email", width: 260, sortable: true, filterType: "text",
      accessor: (u) => `${u.name || ""} ${u.email || ""}`,
      render: (u) => (
        <button
          onClick={() => setInspecting(u)}
          className="text-left min-w-0 block w-full"
          data-testid={`user-row-open-${u.id}`}
        >
          <div className="font-semibold truncate">{u.name || "—"}</div>
          <div className="text-[10px] text-muted-foreground truncate">{u.email || "—"}</div>
        </button>
      ),
      exportValue: (u) => `${u.name || ""} <${u.email || ""}>`,
    },
    { key: "user_type", label: "Type", width: 130, groupable: true, filterType: "select",
      filterOptions: Object.entries(USER_TYPE_LABELS).map(([k, l]) => ({ value: k, label: l })),
      render: (u) => (
        <div className="flex flex-col gap-0.5">
          <span className="chip text-[10px] w-fit">{USER_TYPE_LABELS[u.user_type] || u.user_type}</span>
          <span className="chip text-[10px] w-fit chip-internal">{u.source}</span>
        </div>
      ),
      exportValue: (u) => USER_TYPE_LABELS[u.user_type] || u.user_type,
    },
    { key: "role", label: "Role", width: 160, groupable: true, filterType: "select",
      filterOptions: (roleOptions || []).map((o) => ({ value: o.v, label: o.label })),
      render: (u) => {
        // Feb 2026 · If the member's stored slug isn't in the fetched
        // options (deleted / renamed / the workspace_roles collection is
        // out of sync in prod), inject a read-only synthetic entry so the
        // Select still renders the current value AND the admin can pick a
        // real replacement instead of staring at an empty control.
        const knownSlugs = new Set((roleOptions || []).map((o) => o.v));
        const currentSlug = u.role || "member";
        const rowOptions = knownSlugs.has(currentSlug)
          ? roleOptions
          : [...roleOptions,
             { v: currentSlug, label: `${currentSlug} (missing)`,
                orphan: true }];
        return isAdmin && u.source === "internal" ? (
          <Select value={currentSlug} onValueChange={async (v) => {
            try { await api.patch(`/workspaces/${activeWorkspaceId}/users-admin/${u.id}`, { role: v }); toast.success("Role updated"); load(); }
            catch (e) { toast.error(apiErrorMessage(e, "Could not update role")); }
          }}>
            <SelectTrigger className={`h-7 text-[11px] ${rowOptions.find((o) => o.v === currentSlug)?.orphan ? "text-red-600 border-red-400" : ""}`}
                            data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {rowOptions.map((o) => (
                <SelectItem key={o.v} value={o.v}
                             className={o.orphan ? "text-red-600" : ""}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className={`chip text-[10px] ${rowOptions.find((o) => o.v === currentSlug)?.orphan ? "chip-rejected" : ""}`} title={u.role || ""}>{roleLabelFor(u.role)}</span>
        );
      },
      exportValue: (u) => roleLabelFor(u.role),
    },
    { key: "client_name", label: "Client / org", width: 170, sortable: true, filterType: "text",
      render: (u) => <span className="text-xs truncate block">{u.client_name || "—"}</span>,
      exportValue: (u) => u.client_name || "",
    },
    { key: "is_active", label: "Status", width: 130, groupable: true, filterType: "select",
      filterOptions: [
        { value: true, label: "Active" }, { value: false, label: "Inactive" },
      ],
      accessor: (u) => u.is_active !== false,
      render: (u) => (
        <div className="flex items-center gap-1">
          {isAdmin && u.source === "internal" ? (
            <label className="inline-flex items-center gap-1 cursor-pointer" title={u.is_active === false ? "Activate" : "Deactivate"}>
              <input
                type="checkbox"
                checked={u.is_active !== false}
                onChange={async (e) => {
                  try { await api.patch(`/workspaces/${activeWorkspaceId}/users-admin/${u.id}`, { is_active: e.target.checked }); toast.success(e.target.checked ? "User activated" : "User deactivated"); load(); }
                  catch (err) { toast.error(apiErrorMessage(err, "Update failed")); }
                }}
                data-testid={`user-active-${u.id}`}
                className="w-3.5 h-3.5"
              />
              <span className={`chip text-[10px] ${u.is_active === false ? "chip-rejected" : "chip-approved"}`}>{u.is_active === false ? "inactive" : "active"}</span>
            </label>
          ) : (
            <span className={`chip text-[10px] ${u.is_active === false ? "chip-rejected" : "chip-approved"}`}>{u.is_active === false ? "inactive" : "active"}</span>
          )}
          {u.two_factor_enabled && <span className="chip text-[10px] chip-pending" title="2FA enabled">2FA</span>}
        </div>
      ),
      exportValue: (u) => u.is_active === false ? "inactive" : "active",
    },
  ], [isAdmin, activeWorkspaceId, roleOptions, roleLabelFor]);

  return (
    <>
      <PageHeader
        eyebrow="HR & Org"
        title="User management"
        description="Internal team + external client/vendor users in one console. Add users, change roles, toggle active, reset passwords, impersonate."
        testid="users-header"
        actions={isAdmin && (
          <Button className="rounded-none" onClick={() => setCreating(true)} data-testid="users-add-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add user
          </Button>
        )}
      />
      <div className="px-4 sm:px-6 md:px-10">
        {/* Feb 2026 · Surface roles-API failures instead of silently
            falling back to built-ins. Fires when the endpoint 4xx/5xx'd
            OR when it returned zero rows — either way, admins should
            know their custom roles are hidden. */}
        {rolesApiOk === false && (
          <div className="mb-3 border-l-4 border-amber-500 bg-amber-500/10 px-3 py-2 text-xs flex items-center justify-between gap-3"
                data-testid="roles-load-warning">
            <div>
              <span className="font-semibold text-amber-800">Custom roles couldn't be loaded.</span>
              <span className="ml-1 text-muted-foreground">
                Showing built-in roles only — your custom roles may exist but the API is unreachable.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={loadRoles}
                       className="underline text-primary"
                       data-testid="roles-load-retry">Retry</button>
              <a href="/app/roles" className="underline text-primary"
                  data-testid="roles-load-open">Open Roles page</a>
            </div>
          </div>
        )}
        {rolesApiOk === true && rolesApiEmpty && (
          <div className="mb-3 border-l-4 border-orange-500 bg-orange-500/10 px-3 py-2 text-xs flex items-center justify-between gap-3"
                data-testid="roles-empty-warning">
            <div>
              <span className="font-semibold text-orange-800">This workspace has no roles configured.</span>
              <span className="ml-1 text-muted-foreground">
                Only built-in fallback roles are shown. Head to Roles to seed workspace defaults.
              </span>
            </div>
            <a href="/app/roles" className="underline text-primary"
                data-testid="roles-empty-open">Open Roles page</a>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <StatTile label="Total users" count={counts.all} active={!filterSource} onClick={() => setFilterSource("")} testid="users-stat-all" />
          <StatTile label="Internal" count={counts.internal} active={filterSource === "internal"} onClick={() => setFilterSource(filterSource === "internal" ? "" : "internal")} testid="users-stat-internal" />
          <StatTile label="External (clients/contacts)" count={counts.contact} active={filterSource === "contact"} onClick={() => setFilterSource(filterSource === "contact" ? "" : "contact")} testid="users-stat-contact" />
        </div>
      </div>
      <div className="px-4 sm:px-6 md:px-10 py-6" data-testid="users-table">
        <AdvancedTable
          key={`users-table-${roleOptions.length}`}
          tableKey="users"
          title="Users"
          rows={filtered}
          columns={columns}
          loading={busy && rows.length === 0}
          emptyTitle="No users"
          emptyDescription="Add teammates or invite external users to get started."
          selectable={isAdmin}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          renderBulkBar={(sel) => {
            // Only internal + non-self members are actionable.
            const actionable = sel.filter((u) => u.source === "internal" && u.id !== me?.id);
            const skipped = sel.length - actionable.length;
            const run = (action, label) => setBulkConfirm({
              action, label,
              ids: actionable.map((u) => u.id),
              skipped,
            });
            return (
              <>
                <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs"
                        disabled={!actionable.length || bulkBusy}
                        onClick={() => run("activate", "Activate")}
                        data-testid="users-bulk-activate">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Activate
                </Button>
                <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs"
                        disabled={!actionable.length || bulkBusy}
                        onClick={() => run("deactivate", "Deactivate")}
                        data-testid="users-bulk-deactivate">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Deactivate
                </Button>
                <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs text-destructive hover:text-destructive"
                        disabled={!actionable.length || bulkBusy}
                        onClick={() => run("remove", "Remove from workspace")}
                        data-testid="users-bulk-remove">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
                <BulkRolePicker roleOptions={roleOptions}
                                 disabled={!actionable.length || bulkBusy}
                                 onPick={(role, label) => setBulkConfirm({
                                    action: "role", label, role,
                                    ids: actionable.map((u) => u.id),
                                    skipped,
                                 })} />
                {skipped > 0 && (
                  <span className="text-[10px] text-muted-foreground" data-testid="users-bulk-skip-hint">
                    {skipped} skipped (external / self)
                  </span>
                )}
              </>
            );
          }}
          renderRowActions={(u) => (
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => setInspecting(u)} data-testid={`user-inspect-${u.id}`}><Eye className="w-3.5 h-3.5" /></Button>
              {canGiveFeedback && u.source === "internal" && u.id !== me?.id && (
                <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-amber-700 hover:text-amber-800"
                        title="Give feedback"
                        onClick={() => window.dispatchEvent(new CustomEvent("open-feedback-dialog", { detail: { user_id: u.id, name: u.name || u.email } }))}
                        data-testid={`user-feedback-${u.id}`}>
                  <MessageSquare className="w-3.5 h-3.5" />
                </Button>
              )}
              {isAdmin && u.source === "internal" && u.id !== me?.id && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive hover:text-destructive" data-testid={`user-remove-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {u.name || u.email} from this workspace?</AlertDialogTitle>
                      <AlertDialogDescription>
                        They will lose access to this workspace immediately. Their user account remains active for any other workspaces they belong to. This cannot be undone here.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={async () => {
                        try { await api.delete(`/workspaces/${activeWorkspaceId}/users-admin/${u.id}`); toast.success("User removed from workspace"); load(); }
                        catch (e) { toast.error(apiErrorMessage(e, "Remove failed")); }
                      }} data-testid={`user-remove-confirm-${u.id}`}>Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        />
      </div>

      {inspecting && (
        <UserInspector
          user={inspecting}
          wsId={activeWorkspaceId}
          roleOptions={roleOptions}
          canImpersonate={(isOwner || can?.("workspace.member.impersonate") || can?.("workspace.admin") || can?.("workspace.manage")) && inspecting.id !== me?.id}
          onClose={() => setInspecting(null)}
          onChanged={load}
        />
      )}

      {creating && (
        <AddUserDialog
          wsId={activeWorkspaceId}
          roleOptions={roleOptions}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}

      {bulkConfirm && (
        <AlertDialog open onOpenChange={(v) => { if (!v) setBulkConfirm(null); }}>
          <AlertDialogContent data-testid="users-bulk-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{bulkConfirm.label} {bulkConfirm.ids.length} user{bulkConfirm.ids.length === 1 ? "" : "s"}?</AlertDialogTitle>
              <AlertDialogDescription>
                {bulkConfirm.action === "remove" && "They lose access to this workspace immediately. Their user account remains active for any other workspaces they belong to."}
                {bulkConfirm.action === "deactivate" && "Their sign-in will be blocked and their email threads will be offboarded so nothing dies with their inbox."}
                {bulkConfirm.action === "activate" && "They can log in again immediately."}
                {bulkConfirm.action === "role" && `Their workspace role will be changed to "${bulkConfirm.role}". The workspace owner is always skipped.`}
                {bulkConfirm.skipped > 0 && ` ${bulkConfirm.skipped} selected row(s) will be skipped (external contacts or your own account).`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="users-bulk-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="users-bulk-confirm-run"
                disabled={bulkBusy}
                onClick={async () => {
                  setBulkBusy(true);
                  try {
                    if (bulkConfirm.action === "role") {
                      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/users-admin/bulk-role`,
                        { user_ids: bulkConfirm.ids, role: bulkConfirm.role });
                      toast.success(`${data.updated_count} promoted to ${bulkConfirm.role}${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
                    } else {
                      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/users-admin/bulk`,
                        { user_ids: bulkConfirm.ids, action: bulkConfirm.action });
                      toast.success(`${data.updated} ${bulkConfirm.label.toLowerCase()}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
                    }
                    setSelectedIds(new Set());
                    setBulkConfirm(null);
                    load();
                  } catch (e) {
                    toast.error(apiErrorMessage(e, "Bulk action failed"));
                  } finally { setBulkBusy(false); }
                }}
              >{bulkBusy ? "Working…" : bulkConfirm.label}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

function AddUserDialog({ wsId, roleOptions, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: (roleOptions && roleOptions[0] && roleOptions[0].v) || "team_member", user_type: "employee", join_date: "", send_invite: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, join_date: form.join_date || null };
      const { data } = await api.post(`/workspaces/${wsId}/users-admin`, payload);
      if (data.is_new_user) setResult(data);
      else {
        const emailed = data.invite_email && !data.invite_email.error;
        toast.success(emailed ? "User added — invite email sent" : "User added to workspace");
        onCreated?.();
      }
    } catch (err) { toast.error(apiErrorMessage(err, "Could not add user")); }
    finally { setBusy(false); }
  };

  if (result) {
    const emailed = result.invite_email && !result.invite_email.error;
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onCreated?.(); }}>
        <DialogContent className="max-w-md" data-testid="add-user-temp-pwd-dialog">
          <DialogHeader>
            <DialogTitle>User created{emailed ? " — invite email on the way" : " · share their temp password"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <strong>{result.email}</strong> has been added with the role <span className="chip text-[10px]">{((roleOptions || []).find((o) => o.v === result.role) || {}).label || result.role}</span>.
            </div>
            {emailed && (
              <div className="bg-emerald-500/10 border border-emerald-500/40 p-3 text-xs">
                <div className="font-semibold text-emerald-700 mb-1">Set-password link emailed</div>
                <div>The user will receive an email with a link to choose their password (valid for 14 days). No temp password needed.</div>
              </div>
            )}
            {!emailed && result.invite_email?.error && (
              <div className="bg-amber-500/10 border border-amber-500/40 p-2 text-[11px]">
                <strong>Heads up:</strong> Invite email failed ({result.invite_email.error}). Share the temp password below instead.
              </div>
            )}
            <div className="bg-amber-500/10 border border-amber-500/40 p-3">
              <div className="text-xs mb-2">Temporary password — shown ONCE. Only needed if the user can&apos;t click the email link.</div>
              <div className="flex items-center gap-2">
                <code className="font-mono text-base bg-background px-2 py-1 flex-1" data-testid="add-user-temp-pwd">{result.temp_password}</code>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => { navigator.clipboard.writeText(result.temp_password); toast.success("Copied"); }} data-testid="add-user-temp-pwd-copy"><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-none" onClick={() => onCreated?.()} data-testid="add-user-done">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add user to workspace</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10" data-testid="add-user-email" placeholder="person@company.com" />
            <p className="text-[10px] text-foreground/50 mt-1">If this email already has an account they&apos;ll just be added as a member. Otherwise we&apos;ll create an account and email them a set-password link.</p>
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" data-testid="add-user-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-10" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(roleOptions || []).map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Type</Label>
              <Select value={form.user_type} onValueChange={(v) => setForm({ ...form, user_type: v })}>
                <SelectTrigger className="h-10" data-testid="add-user-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(USER_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Join date <span className="text-foreground/40 normal-case">(optional)</span></Label>
            <Input
              type="date"
              value={form.join_date}
              onChange={(e) => setForm({ ...form, join_date: e.target.value })}
              className="h-10"
              data-testid="add-user-join-date"
            />
            <p className="text-[10px] text-foreground/50 mt-1">
              With a join date, the invite email uses the onboarding welcome copy referencing the start date. Without it, we send a plain &quot;account is ready&quot; email.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} data-testid="add-user-send-invite" />
            Email a set-password link now
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={onClose} data-testid="add-user-cancel">Cancel</Button>
            <Button type="submit" className="rounded-none" disabled={busy} data-testid="add-user-submit">{busy ? "Adding…" : "Add user"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserInspector({ user, wsId, roleOptions, canImpersonate, onClose, onChanged }) {
  const { user: me } = useAuth();
  const roleLabel = useMemo(() => {
    const opt = (roleOptions || []).find((o) => o.v === user.role);
    return opt ? opt.label : (user.role || "—");
  }, [roleOptions, user.role]);
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("info");
  const [resetPwd, setResetPwd] = useState(null);
  // Iter 253s · Ad-hoc feedback state.
  const [canGiveFeedback, setCanGiveFeedback] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    if (user.source === "internal") {
      api.get(`/workspaces/${wsId}/users-admin/${user.id}/access`).then((r) => setAccess(r.data)).catch(() => { });
    }
    // Only show the Give feedback button when the caller has the
    // feedback.create_internal permission (owners/admins always do) AND the
    // target is not the caller themselves.
    if (user.id !== me?.id) {
      api.get(`/workspaces/${wsId}/feedback/can-create-internal`).then(
        (r) => setCanGiveFeedback(!!r.data?.allowed)).catch(() => setCanGiveFeedback(false));
    } else {
      setCanGiveFeedback(false);
    }
  }, [user.id, wsId, user.source, me?.id]);

  const patch = async (payload) => {
    try {
      await api.patch(`/workspaces/${wsId}/users-admin/${user.id}`, payload);
      toast.success("Updated");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const reset = async () => {
    if (!confirm(`Reset password for ${user.name}? They'll be forced to change on next login.`)) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/users-admin/${user.id}/reset-password`);
      setResetPwd(data.temp_password);
    } catch (e) { toast.error(apiErrorMessage(e, "Reset failed")); }
  };
  const emailSetupLink = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/users-admin/${user.id}/resend-setup-email`);
      const overall = data?.delivery?.sent ?? data?.delivery?.overall;
      if (overall === true || overall === "sent") toast.success(`Set-password link emailed to ${user.email}`);
      else toast.success("Set-password link generated — email delivery pending");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not email set-password link")); }
  };
  const emailResetLink = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/users-admin/${user.id}/send-reset-email`);
      const overall = data?.delivery?.sent ?? data?.delivery?.overall;
      if (overall === true || overall === "sent") toast.success(`Password-reset email sent to ${user.email}`);
      else toast.success("Reset link generated — email delivery pending");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not email reset link")); }
  };
  const impersonate = async () => {
    if (!confirm(`Impersonate ${user.name}? You'll act AS this user for 60 minutes. All actions are logged with your owner id.`)) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/users-admin/${user.id}/impersonate`);
      // Stash original token
      const original = localStorage.getItem("co_token");
      sessionStorage.setItem("co_impersonation_origin", original);
      sessionStorage.setItem("co_impersonation_target", JSON.stringify(data.target));
      localStorage.setItem("co_token", data.token);
      toast.success(`Impersonating ${data.target.name}. Reloading…`);
      setTimeout(() => { window.location.href = "/app"; }, 800);
    } catch (e) { toast.error(apiErrorMessage(e, "Impersonate failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="user-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Users className="w-4 h-4" />
            <span>{user.name}</span>
            <span className="chip text-[10px]">{USER_TYPE_LABELS[user.user_type] || user.user_type}</span>
            <div className="ml-auto flex items-center gap-2">
              {canGiveFeedback && (
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setFeedbackOpen(true)}
                        data-testid="user-give-feedback">
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Give feedback
                </Button>
              )}
              {canImpersonate && <Button size="sm" className="rounded-none" onClick={impersonate} data-testid="user-impersonate"><UserCheck className="w-3.5 h-3.5 mr-1" /> Impersonate</Button>}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-border mb-3">
          <TabBtn active={tab === "info"} onClick={() => setTab("info")} testid="user-tab-info">Profile</TabBtn>
          {user.source === "internal" && <TabBtn active={tab === "access"} onClick={() => setTab("access")} testid="user-tab-access">Access</TabBtn>}
          {user.source === "internal" && <TabBtn active={tab === "security"} onClick={() => setTab("security")} testid="user-tab-security">Security</TabBtn>}
          {canImpersonate && <TabBtn active={tab === "impersonation"} onClick={() => setTab("impersonation")} testid="user-tab-impersonation">Recent impersonations</TabBtn>}
        </div>

        {tab === "info" && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <KV label="Email">{user.email || "—"}</KV>
              <KV label="Source">{user.source}</KV>
              <KV label="Workspace role">
                <span className="inline-flex items-center gap-2">
                  <span>{roleLabel}</span>
                  {user.role && user.role !== roleLabel && (
                    <code className="text-[10px] text-foreground/50 font-mono">{user.role}</code>
                  )}
                </span>
              </KV>
              <KV label="User type">
                <Select value={user.user_type || "employee"} onValueChange={(v) => patch({ user_type: v })}>
                  <SelectTrigger className="h-9 text-xs" data-testid="user-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(USER_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </KV>
              {user.client_name && <KV label="Client">{user.client_name}</KV>}
              <KV label="Last login">{user.last_login_at ? fmtRelative(user.last_login_at) : "—"}</KV>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={user.is_active !== false} onChange={(e) => patch({ is_active: e.target.checked })} data-testid="user-active-toggle" /> Active
            </label>
          </div>
        )}

        {tab === "access" && (
          <div className="space-y-4" data-testid="user-access-panel">
            {!access ? <div className="text-sm">Loading…</div> : (
              <>
                <Section title="Departments">
                  {access.departments?.length === 0 ? <div className="text-xs text-muted-foreground">Not assigned to any department.</div>
                    : <div className="flex flex-wrap gap-1">{access.departments.map((d) => <span key={d.id} className="chip text-[10px]">{d.name}</span>)}</div>}
                </Section>
                <Section title={`Projects (${access.projects?.length || 0})`}>
                  {access.projects?.length === 0 ? <div className="text-xs text-muted-foreground">No project assignments.</div>
                    : (
                      <div className="border border-border" data-testid="user-projects-table">
                        {access.projects.map((p) => (
                          <div key={p.id} className="px-3 py-2 border-b border-border text-sm flex items-center gap-2">
                            <span className="font-medium flex-1 truncate">{p.name || p.id}</span>
                            {p.status && <span className="chip text-[10px]">{p.status}</span>}
                            <span className="chip text-[10px] chip-internal">{p.role_in_project}</span>
                          </div>
                        ))}
                      </div>
                    )}
                </Section>
                <Section title={`Vault grants (${access.credential_grants?.length || 0})`}>
                  {access.credential_grants?.length === 0 ? <div className="text-xs text-muted-foreground">No credential grants.</div>
                    : (
                      <div className="flex flex-wrap gap-1">
                        {access.credential_grants.map((g) => <span key={g.id} className="chip text-[10px]" title={`${g.permission}`}>🔑 {g.target_type === "department" ? "🏢" : "👤"} {g.permission}</span>)}
                      </div>
                    )}
                </Section>
              </>
            )}
          </div>
        )}

        {tab === "security" && (
          <div className="space-y-4">
            <div className="border border-border p-4 space-y-3" data-testid="security-2fa">
              <div className="label-eyebrow flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Two-factor authentication</div>
              <p className="text-xs text-muted-foreground">
                {user.two_factor_enabled ? "User has 2FA enabled on their account." : "User has not enabled 2FA yet."}
              </p>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!user.force_2fa} onChange={(e) => patch({ force_2fa: e.target.checked })} data-testid="user-force-2fa" />
                Require 2FA for this user (admin policy)
              </label>
            </div>
            <div className="border border-border p-4 space-y-3" data-testid="security-password">
              <div className="label-eyebrow flex items-center gap-2"><KeyRound className="w-3 h-3" /> Password reset</div>
              {resetPwd ? (
                <div className="bg-amber-500/10 border border-amber-500/40 p-3">
                  <div className="text-xs mb-2">Temporary password — shown once. Share via a secure channel.</div>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-base bg-background px-2 py-1 flex-1" data-testid="reset-pwd">{resetPwd}</code>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => { navigator.clipboard.writeText(resetPwd); toast.success("Copied"); }} data-testid="reset-pwd-copy"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" className="rounded-none" onClick={emailResetLink} data-testid="reset-pwd-email">Or: email a reset link instead</Button>
                    <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setResetPwd(null)}>Done</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">The reset link lets the user pick their own password without you handing over the temp one.</p>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={reset} variant="outline" className="rounded-none" data-testid="user-reset-pwd"><KeyRound className="w-3.5 h-3.5 mr-2" /> Generate temp password</Button>
                  <Button onClick={emailResetLink} variant="outline" className="rounded-none" data-testid="user-email-reset-link">Email password-reset link</Button>
                  <Button onClick={emailSetupLink} variant="outline" className="rounded-none" data-testid="user-email-setup-link">Resend set-password invite</Button>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Pick a temp password when you need to hand it over verbally. Otherwise, the two email buttons are safer — the user picks their own password.
              </p>
            </div>
          </div>
        )}
        {tab === "impersonation" && (
          <ImpersonationAuditPanel wsId={wsId} userId={user.id} />
        )}
      </DialogContent>
      {feedbackOpen && (
        <AdHocFeedbackDialog
          toUserId={user.id}
          toUserName={user.name}
          onClose={() => setFeedbackOpen(false)}
          onSaved={() => setFeedbackOpen(false)}
        />
      )}
    </Dialog>
  );
}

// Feb 2026 · Recent impersonation audit for a target user. Compact
// list: who impersonated, when, duration (if ended), reason if any.
// Uses GET /workspaces/{ws}/users-admin/{uid}/impersonation-audit
// which is admin / workspace.member.impersonate gated on the backend.
function ImpersonationAuditPanel({ wsId, userId }) {
  const [events, setEvents] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setErr(null);
    setEvents(null);
    api.get(`/workspaces/${wsId}/users-admin/${userId}/impersonation-audit?days=${days}&limit=20`)
       .then((r) => { if (alive) setEvents(r.data?.events || []); })
       .catch((e) => { if (alive) setErr(apiErrorMessage(e, "Couldn't load impersonation audit")); });
    return () => { alive = false; };
  }, [wsId, userId, days]);
  const fmtWhen = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  const fmtDuration = (start, end) => {
    if (!start || !end) return null;
    const ms = new Date(end) - new Date(start);
    if (!Number.isFinite(ms) || ms < 0) return null;
    const m = Math.round(ms / 60000);
    if (m < 1) return "<1m";
    if (m < 60) return `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  };
  return (
    <div className="space-y-3" data-testid="impersonation-audit-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="label-eyebrow flex-1 flex items-center gap-2">
          <UserCheck className="w-3 h-3" /> Recent impersonations
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`text-[10px] px-2 py-1 border transition-colors ${d === days ? "bg-primary/10 border-primary text-primary" : "border-border hover:border-primary/60"}`}
              data-testid={`impersonation-audit-range-${d}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-xs text-rose-600 bg-rose-500/10 border border-rose-500/40 p-2">{err}</div>}
      {!err && events === null && (
        <div className="text-xs text-muted-foreground italic">Loading…</div>
      )}
      {!err && Array.isArray(events) && events.length === 0 && (
        <div className="text-xs text-muted-foreground italic border border-dashed border-border p-4 text-center" data-testid="impersonation-audit-empty">
          Nobody impersonated this user in the last {days} days.
        </div>
      )}
      {Array.isArray(events) && events.length > 0 && (
        <ul className="space-y-1.5" data-testid="impersonation-audit-list">
          {events.map((e) => {
            const dur = fmtDuration(e.started_at, e.ended_at);
            return (
              <li key={e.id} className="flex items-start gap-2 p-2 border border-border hover:bg-muted/40" data-testid={`impersonation-audit-item-${e.id}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.impersonator_name || e.impersonator_id?.slice(0, 8) || "Unknown admin"}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                    <span>{fmtWhen(e.started_at)}</span>
                    {dur && <span className="border border-border px-1 py-px text-[9px]">{dur}</span>}
                    {!e.ended_at && <span className="text-primary text-[9px] uppercase tracking-widest">active</span>}
                    {e.reason && <span className="italic truncate">· {e.reason}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{title}</div>
      {children}
    </div>
  );
}
function KV({ label, children }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="text-sm mt-0.5">{children}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return <button onClick={onClick} className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-b-2 ${active ? "border-primary font-bold" : "border-transparent text-muted-foreground"}`} data-testid={testid}>{children}</button>;
}
function StatTile({ label, count, active, onClick, testid }) {
  return (
    <button onClick={onClick} className={`border ${active ? "border-primary bg-primary/10" : "border-border"} px-3 py-2 text-left transition-colors hover:bg-muted`} data-testid={testid}>
      <div className="label-eyebrow text-[10px] truncate">{label}</div>
      <div className="text-xl font-bold mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{count}</div>
    </button>
  );
}

// Feb 2026 · Compact role picker used inside the bulk-action bar. Opens
// on click and shows every non-orphan role; clicking one dispatches the
// bulk-role change.
function BulkRolePicker({ roleOptions, disabled, onPick }) {
  const [open, setOpen] = useState(false);
  const selectable = (roleOptions || []).filter((o) => o.is_active !== false && !o.orphan);
  return (
    <div className="relative inline-block">
      <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs"
              disabled={disabled} onClick={() => setOpen((o) => !o)}
              data-testid="users-bulk-role-btn">
        <Shield className="w-3.5 h-3.5 mr-1" /> Change role…
      </Button>
      {open && (
        <div className="absolute z-30 top-8 left-0 bg-white border border-border shadow-lg min-w-[200px] max-h-72 overflow-auto"
              onMouseLeave={() => setOpen(false)} data-testid="users-bulk-role-menu">
          {selectable.map((o) => (
            <button key={o.v} onClick={() => { onPick(o.v, `Change role to ${o.l}`); setOpen(false); }}
                     data-testid={`users-bulk-role-${o.v}`}
                     className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center justify-between">
              <span>{o.l}</span>
              {o.v === "studio_manager" && <span className="text-[9px] text-primary uppercase tracking-widest">preset</span>}
            </button>
          ))}
          {selectable.length === 0 && <div className="text-[11px] italic p-2 text-muted-foreground">No roles available. Add one via /app/roles.</div>}
        </div>
      )}
    </div>
  );
}

